import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Calculate as CalculateIcon,
  Notes as NotesIcon,
  Percent as PercentIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import { updateExistingSalaryConfiguration } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import editSalaryConfigSvg from "assets/modals/edit.svg";

const EditSalaryConfigurationModal = ({
  isOpen,
  onClose,
  salaryConfig,
  onSuccess,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchEmployees());
      const originalOverflow = document.body.style.overflow;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = "0px";
      };
    }
  }, [isOpen, dispatch]);

  const [loading, setLoading] = useState(false);
  const [isAttendanceBased, setIsAttendanceBased] = useState(false);

  const attendanceSummary = {
    total_present_days: salaryConfig?.total_present_days || 0,
    total_absent_days: salaryConfig?.total_absent_days || 0,
    total_leave_days: salaryConfig?.total_leave_days || 0,
  };
  const attendanceConfirmationId =
    salaryConfig?.attendance_confirmation_id || null;

  // Helper function to calculate salary components
  const calculateSalaryComponents = (data) => {
    const basic = parseFloat(data.basic) || 0;
    const hrAllowance = parseFloat(data.hr_allowance) || 0;
    const specialAllowance = parseFloat(data.special_allowance) || 0;
    const conveyanceAllowance = parseFloat(data.conveyance_allowance) || 0;
    const mobileAllowance = parseFloat(data.mobile_allowance) || 0;
    const otherAllowance = parseFloat(data.other_allowance) || 0;
    const da = parseFloat(data.da) || 0;
    const incentive = parseFloat(data.incentive) || 0;
    const travelAllowance = parseFloat(data.travel_allowance) || 0;
    const bonus = parseFloat(data.bonus) || 0;
    const mediclaim = parseFloat(data.mediclaim) || 0;

    // 1. Calculate Gross Salary (sum of all earnings)
    const grossSalary =
      basic +
      hrAllowance +
      specialAllowance +
      conveyanceAllowance +
      mobileAllowance +
      otherAllowance +
      da +
      incentive +
      travelAllowance;

    // Determine which salary to use for calculations
    const attendanceBasedSalary = parseFloat(data.attendance_based_salary) || 0;
    const isAttendanceBased = attendanceBasedSalary > 0;
    const baseSalary = isAttendanceBased ? attendanceBasedSalary : grossSalary;

    // 2. Calculate TDS
    let tds = 0;
    let tdsPercentage = 0;
    if (data.tds_type === "number") {
      tds = parseFloat(data.tds) || 0;
      tdsPercentage = baseSalary > 0 ? (tds / baseSalary) * 100 : 0;
    } else {
      tdsPercentage = parseFloat(data.tds_percentage) || 0;
      tds = (baseSalary * tdsPercentage) / 100;
    }

    // 3. Calculate PF
    let pfEmployee = 0;
    let pfPercentage =
      parseFloat(data.pf_percentage) !== undefined &&
      parseFloat(data.pf_percentage) !== null &&
      !isNaN(parseFloat(data.pf_percentage))
        ? parseFloat(data.pf_percentage)
        : 12;
    let pfAmount = parseFloat(data.pf_amount) || 0;
    if (data.pf_applicable === 1) {
      if (data.pf_type === "number") {
        pfEmployee = pfAmount;
        pfPercentage = baseSalary > 0 ? (pfAmount / baseSalary) * 100 : 0;
      } else {
        pfEmployee = (baseSalary * pfPercentage) / 100;
        pfAmount = pfEmployee;
      }
    }

    // 4. Calculate ESI
    let esiEmployee = 0;
    let esiPercentage =
      parseFloat(data.esi_percentage) !== undefined &&
      parseFloat(data.esi_percentage) !== null &&
      !isNaN(parseFloat(data.esi_percentage))
        ? parseFloat(data.esi_percentage)
        : 0.75;
    let esiAmount = parseFloat(data.esi_amount) || 0;
    if (data.esi_applicable === 1) {
      if (data.esi_type === "number") {
        esiEmployee = esiAmount;
        esiPercentage = baseSalary > 0 ? (esiAmount / baseSalary) * 100 : 0;
      } else {
        esiEmployee = (baseSalary * esiPercentage) / 100;
        esiAmount = esiEmployee;
      }
    }

    // 5. Professional Tax (PT)
    let pt = 0;
    let ptPercentage = parseFloat(data.pt_percentage) || 0;
    let ptAmount =
      parseFloat(data.pt_amount) !== undefined &&
      parseFloat(data.pt_amount) !== null &&
      !isNaN(parseFloat(data.pt_amount))
        ? parseFloat(data.pt_amount)
        : 200;
    if (data.pt_applicable === 1) {
      if (data.pt_type === "number") {
        pt = ptAmount;
        ptPercentage = baseSalary > 0 ? (ptAmount / baseSalary) * 100 : 0;
      } else {
        pt = (baseSalary * ptPercentage) / 100;
        ptAmount = pt;
      }
    }
    const additionalDeduction = parseFloat(data.deduction_amount) || 0;

    // 6. Total Deduction (sum of all deductions)
    const totalDeduction =
      tds + pfEmployee + esiEmployee + pt + additionalDeduction;

    // 7. CTC Component (Base Salary + Bonus + Mediclaim)
    const totalCtcComponent =
      baseSalary + parseFloat(bonus) + parseFloat(mediclaim);

    // 8. Net Salary (CTC - Total Deduction)
    const netSalary = totalCtcComponent - totalDeduction;

    return {
      grossSalary: grossSalary.toFixed(2),
      tds: tds.toFixed(2),
      tdsPercentage: tdsPercentage.toFixed(2),
      pfEmployee: pfEmployee.toFixed(2),
      pfPercentage: pfPercentage.toFixed(2),
      pfAmount: pfAmount.toFixed(2),
      esiEmployee: esiEmployee.toFixed(2),
      esiPercentage: esiPercentage.toFixed(2),
      esiAmount: esiAmount.toFixed(2),
      pt: pt.toFixed(2),
      ptPercentage: ptPercentage.toFixed(2),
      ptAmount: ptAmount.toFixed(2),
      attendance_based_salary: attendanceBasedSalary,
      totalDeduction: totalDeduction.toFixed(2),
      netSalary: netSalary.toFixed(2),
      totalCtcComponent: totalCtcComponent.toFixed(2),
      additionalDeduction: additionalDeduction.toFixed(2),
    };
  };

  const [formData, setFormData] = useState({
    employee_id: "",
    salary_structure_id: "",
    basic: "",
    hr_allowance: "",
    special_allowance: "",
    conveyance_allowance: "",
    mobile_allowance: "",
    other_allowance: "",
    da: "",
    incentive: "",
    travel_allowance: "",
    gross_salary: "",
    tds_percentage: "",
    tds: "",
    tds_type: "number",
    pf_applicable: 0,
    pf_type: "number",
    pf_percentage: "",
    pf_amount: "",
    esi_applicable: 0,
    esi_type: "number",
    esi_percentage: "",
    esi_amount: "",
    pt_applicable: 0,
    pt_type: "number",
    pt_percentage: "",
    pt_amount: "",
    bonus: "",
    mediclaim: "",
    attendance_based_salary: 0,
    total_ctc_component: "",
    total_deduction: "",
    net_salary: "",
    status: "active",
    deduction_name: "",
    deduction_amount: "",
    note: "",
  });

  useEffect(() => {
    if (salaryConfig) {
      const attendanceBasedSalary = salaryConfig.attendance_based_salary || 0;
      setIsAttendanceBased(attendanceBasedSalary > 0);

      setFormData({
        employee_id: salaryConfig.employee_id || "",
        salary_structure_id: salaryConfig.salary_structure_id || "",
        basic: salaryConfig.basic || "",
        hr_allowance: salaryConfig.hr_allowance || "",
        special_allowance: salaryConfig.special_allowance || "",
        conveyance_allowance: salaryConfig.conveyance_allowance || "",
        mobile_allowance: salaryConfig.mobile_allowance || "",
        other_allowance: salaryConfig.other_allowance || "",
        da: salaryConfig.da || "",
        incentive: salaryConfig.incentive || "",
        travel_allowance: salaryConfig.travel_allowance || "",
        gross_salary: salaryConfig.gross_salary || "",
        tds_percentage: salaryConfig.tds_percentage || "",
        tds: salaryConfig.tds || "",
        tds_type: salaryConfig.tds_type || "number",
        pf_applicable:
          salaryConfig.pf_applicable !== undefined
            ? salaryConfig.pf_applicable
            : 0,
        pf_type: salaryConfig.pf_type || "number",
        pf_percentage: salaryConfig.pf_percentage || "",
        pf_amount: salaryConfig.pf_amount || "",
        esi_applicable:
          salaryConfig.esi_applicable !== undefined
            ? salaryConfig.esi_applicable
            : 0,
        esi_type: salaryConfig.esi_type || "number",
        esi_percentage: salaryConfig.esi_percentage || "",
        esi_amount: salaryConfig.esi_amount || "",
        pt_applicable:
          salaryConfig.pt_applicable !== undefined
            ? salaryConfig.pt_applicable
            : 0,
        pt_type: salaryConfig.pt_type || "number",
        pt_percentage: salaryConfig.pt_percentage || "",
        pt_amount: salaryConfig.pt_amount || "",
        bonus: salaryConfig.bonus || "",
        mediclaim: salaryConfig.mediclaim || "",
        attendance_based_salary: attendanceBasedSalary,
        total_ctc_component: salaryConfig.total_ctc_component || "",
        total_deduction: salaryConfig.total_deduction || "",
        net_salary: salaryConfig.net_salary || "",
        status: salaryConfig.status || "active",
        deduction_name: salaryConfig.deduction_name || "",
        deduction_amount: salaryConfig.deduction_amount || "",
        note: salaryConfig.note || "",
      });
    }
  }, [salaryConfig]);

  const [isBasicFilled, setIsBasicFilled] = useState(false);

  useEffect(() => {
    const basicValue = parseFloat(formData.basic);
    setIsBasicFilled(!isNaN(basicValue) && basicValue > 0);
  }, [formData.basic]);
  useEffect(() => {
    // If attendance-based salary is enabled, recalculate the actual amount
    if (isAttendanceBased && salaryConfig) {
      const present = Number(salaryConfig.total_present_days) || 0;
      const absent = Number(salaryConfig.total_absent_days) || 0;
      const leave = Number(salaryConfig.total_leave_days) || 0;
      const workingDays = present + absent + leave;
      const grossSalary = parseFloat(formData.gross_salary) || 0;

      if (workingDays > 0 && grossSalary > 0) {
        const attendanceBasedSalary = (grossSalary / workingDays) * present;

        setFormData((prev) => ({
          ...prev,
          attendance_based_salary: attendanceBasedSalary,
        }));
      }
    }
  }, [
    isAttendanceBased,
    salaryConfig?.total_present_days,
    formData.gross_salary,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle switch fields separately
    if (
      name === "pf_applicable" ||
      name === "esi_applicable" ||
      name === "pt_applicable"
    ) {
      return;
    }

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };

      // Calculate all components
      const calculated = calculateSalaryComponents(newFormData);

      const updatedFields = {
        gross_salary: calculated.grossSalary,
        total_deduction: calculated.totalDeduction,
        net_salary: calculated.netSalary,
        total_ctc_component: calculated.totalCtcComponent,
        attendance_based_salary: prev.attendance_based_salary,
      };

      // Update the TDS fields based on type
      if (newFormData.tds_type === "percentage") {
        if (name !== "tds") {
          updatedFields.tds = calculated.tds;
        }
      } else {
        if (name !== "tds_percentage") {
          updatedFields.tds_percentage = calculated.tdsPercentage;
        }
      }

      // Update PF fields based on type
      if (newFormData.pf_type === "percentage") {
        if (name !== "pf_amount") {
          updatedFields.pf_amount = calculated.pfAmount;
        }
      } else {
        if (name !== "pf_percentage") {
          updatedFields.pf_percentage = calculated.pfPercentage;
        }
      }

      // Update ESI fields based on type
      if (newFormData.esi_type === "percentage") {
        if (name !== "esi_amount") {
          updatedFields.esi_amount = calculated.esiAmount;
        }
      } else {
        if (name !== "esi_percentage") {
          updatedFields.esi_percentage = calculated.esiPercentage;
        }
      }

      // Update PT fields based on type
      if (newFormData.pt_type === "percentage") {
        if (name !== "pt_amount") {
          updatedFields.pt_amount = calculated.ptAmount;
        }
      } else {
        if (name !== "pt_percentage") {
          updatedFields.pt_percentage = calculated.ptPercentage;
        }
      }

      return {
        ...newFormData,
        ...updatedFields,
      };
    });
  };

  const handleToggleChange = (typeField, value) => {
    if (!value) return; // Prevent deselecting
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [typeField]: value,
      };

      // Calculate based on the new types
      const calculated = calculateSalaryComponents(newFormData);

      return {
        ...newFormData,
        tds: calculated.tds,
        tds_percentage: calculated.tdsPercentage,
        pf_amount: calculated.pfAmount,
        pf_percentage: calculated.pfPercentage,
        esi_amount: calculated.esiAmount,
        esi_percentage: calculated.esiPercentage,
        pt_amount: calculated.ptAmount,
        pt_percentage: calculated.ptPercentage,
        total_deduction: calculated.totalDeduction,
        net_salary: calculated.netSalary,
        total_ctc_component: calculated.totalCtcComponent,
      };
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: checked ? 1 : 0,
      };

      const calculated = calculateSalaryComponents(newFormData);

      return {
        ...newFormData,
        tds: calculated.tds,
        tds_percentage: calculated.tdsPercentage,
        pf_amount: calculated.pfAmount,
        pf_percentage: calculated.pfPercentage,
        esi_amount: calculated.esiAmount,
        esi_percentage: calculated.esiPercentage,
        pt_amount: calculated.ptAmount,
        pt_percentage: calculated.ptPercentage,
        total_deduction: calculated.totalDeduction,
        net_salary: calculated.netSalary,
        total_ctc_component: calculated.totalCtcComponent,
        attendance_based_salary: prev.attendance_based_salary,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formattedData = {
      employee_id: parseInt(formData.employee_id),
      salary_structure_id: parseInt(formData.salary_structure_id) || null,
      basic: parseFloat(formData.basic) || 0,
      hr_allowance: parseFloat(formData.hr_allowance) || 0,
      special_allowance: parseFloat(formData.special_allowance) || 0,
      conveyance_allowance: parseFloat(formData.conveyance_allowance) || 0,
      mobile_allowance: parseFloat(formData.mobile_allowance) || 0,
      other_allowance: parseFloat(formData.other_allowance) || 0,
      da: parseFloat(formData.da) || 0,
      incentive: parseFloat(formData.incentive) || 0,
      travel_allowance: parseFloat(formData.travel_allowance) || 0,
      gross_salary: parseFloat(formData.gross_salary) || 0,
      tds_percentage: parseFloat(formData.tds_percentage) || 0,
      tds: parseFloat(formData.tds) || 0,
      tds_type: formData.tds_type,
      pf_applicable: Number(formData.pf_applicable),
      pf_type: formData.pf_type,
      pf_percentage: parseFloat(formData.pf_percentage) || 0,
      pf_amount: parseFloat(formData.pf_amount) || 0,
      esi_applicable: Number(formData.esi_applicable),
      esi_type: formData.esi_type,
      esi_percentage: parseFloat(formData.esi_percentage) || 0,
      esi_amount: parseFloat(formData.esi_amount) || 0,
      pt_applicable: Number(formData.pt_applicable),
      pt_type: formData.pt_type,
      pt_percentage: parseFloat(formData.pt_percentage) || 0,
      pt_amount: parseFloat(formData.pt_amount) || 0,
      bonus: parseFloat(formData.bonus) || 0,
      mediclaim: parseFloat(formData.mediclaim) || 0,
      attendance_based_salary: isAttendanceBased ? 1 : 0,
      total_ctc_component: parseFloat(formData.total_ctc_component) || 0,
      total_deduction: parseFloat(formData.total_deduction) || 0,
      net_salary: parseFloat(formData.net_salary) || 0,
      status: formData.status,
      deduction_name: formData.deduction_name || null,
      deduction_amount: parseFloat(formData.deduction_amount) || 0,
      note: formData.note || null,
    };

    const result = await dispatch(
      updateExistingSalaryConfiguration(salaryConfig.id, formattedData),
    );

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "primary.main",
      },
    },
  };

  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "primary.main",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#f59e0b",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && salaryConfig && (
        <>
          <style>
            {`
        .MuiPopover-root {
          z-index: 1400 !important;
        }
        .MuiPopper-root {
          z-index: 1400 !important;
        }
        .MuiMenu-root {
          z-index: 1400 !important;
        }
      `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100vh",
              width: "100%",
              maxWidth: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editSalaryConfigSvg}
                      alt="Edit Salary Configuration"
                      sx={{ width: 80, height: 80, objectFit: "contain" }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Edit Salary Configuration
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Update salary details for {salaryConfig.first_name}{" "}
                      {salaryConfig.last_name}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {/* Employee Selection Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <PersonIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 20,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "1px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Employee Details
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          select
                          label="Select Employee"
                          name="employee_id"
                          value={formData.employee_id}
                          onChange={handleChange}
                          required
                          disabled
                          size="small"
                          sx={{
                            ...inputSx,
                            width: 360,
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#1a2332"
                                  : "#f3f4f6",
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{
                            MenuProps: {
                              sx: { zIndex: 1400 },
                              PaperProps: {
                                sx: { zIndex: 1400 },
                              },
                              disableScrollLock: true,
                            },
                          }}
                        >
                          {employees?.map((employee) => (
                            <MenuItem key={employee.id} value={employee.id}>
                              {employee.first_name} {employee.last_name || ""}
                              {employee.employee_code &&
                                ` (${employee.employee_code})`}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Status"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          required
                          size="small"
                          sx={{ ...inputSx, width: 370 }}
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </TextField>
                      </Stack>

                      {/* Confirmation Status */}
                      {salaryConfig?.employee_id && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: attendanceConfirmationId
                              ? theme.palette.mode === "dark"
                                ? "rgba(16,185,129,0.1)"
                                : "#d1fae5"
                              : theme.palette.mode === "dark"
                              ? "rgba(245,158,11,0.1)"
                              : "#fef3c7",
                            border: `1px solid ${
                              attendanceConfirmationId
                                ? theme.palette.mode === "dark"
                                  ? "rgba(16,185,129,0.3)"
                                  : "#6ee7b7"
                                : theme.palette.mode === "dark"
                                ? "rgba(245,158,11,0.3)"
                                : "#fde68a"
                            }`,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                          }}
                        >
                          {attendanceConfirmationId ? (
                            <>
                              <Chip
                                label="✅ Attendance Confirmed"
                                size="small"
                                sx={{
                                  bgcolor: "#d1fae5",
                                  color: "#065f46",
                                  fontWeight: 600,
                                  height: 28,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#6ee7b7"
                                      : "#065f46",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Attendance has been confirmed by HR for this
                                employee
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.75rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#6ee7b7"
                                      : "#065f46",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                ID: {attendanceConfirmationId}
                              </Typography>
                              {/* Attendance Summary */}
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ ml: "auto" }}
                              >
                                <Chip
                                  label={`Present Days : ${attendanceSummary.total_present_days}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "#d1fae5",
                                    color: "#065f46",
                                    fontWeight: 600,
                                  }}
                                />
                                <Chip
                                  label={`Leave Days : ${attendanceSummary.total_leave_days}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "#d1fae5",
                                    color: "#0971f0d5",
                                    fontWeight: 600,
                                  }}
                                />
                                <Chip
                                  label={`Absent Days : ${attendanceSummary.total_absent_days}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "#d1fae5",
                                    color: "#991b1b",
                                    fontWeight: 600,
                                  }}
                                />
                              </Stack>
                            </>
                          ) : (
                            <>
                              <Chip
                                label="⚠️ Not Confirmed"
                                size="small"
                                sx={{
                                  bgcolor: "#fef3c7",
                                  color: "#92400e",
                                  fontWeight: 600,
                                  height: 28,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: "#92400e",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                Attendance not confirmed by HR yet
                              </Typography>
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>

                  {/* Salary Details Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <MoneyIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Salary Details
                      </Typography>
                    </Stack>

                    {/* Row 1: 4 fields */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: 1.5, width: "100%" }}
                    >
                      <Box sx={{ width: "25%" }}>
                        <TextField
                          fullWidth
                          label="Basic Salary (₹)"
                          name="basic"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.basic}
                          onChange={handleChange}
                          required
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "25%" }}>
                        <TextField
                          fullWidth
                          label="HR Allowance (₹)"
                          name="hr_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.hr_allowance}
                          onChange={handleChange}
                          required
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "25%" }}>
                        <TextField
                          fullWidth
                          label="Special Allowance (₹)"
                          name="special_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.special_allowance}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "25%" }}>
                        <TextField
                          fullWidth
                          label="Conveyance Allowance (₹)"
                          name="conveyance_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.conveyance_allowance}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Stack>

                    {/* Row 2: 4 fields */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: 1.5, width: "100%" }}
                    >
                      <Box sx={{ width: "20%" }}>
                        <TextField
                          fullWidth
                          label="Mobile Allowance (₹)"
                          name="mobile_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.mobile_allowance}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>

                      <Box sx={{ width: "20%" }}>
                        <TextField
                          fullWidth
                          label="Dearness Allowance (₹)"
                          name="da"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.da}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "20%" }}>
                        <TextField
                          fullWidth
                          label="Incentive (₹)"
                          name="incentive"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.incentive}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "20%" }}>
                        <TextField
                          fullWidth
                          label="Travel Allowance (₹)"
                          name="travel_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.travel_allowance}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box sx={{ width: "20%" }}>
                        <TextField
                          fullWidth
                          label="Other Allowance (₹)"
                          name="other_allowance"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.other_allowance}
                          onChange={handleChange}
                          size="small"
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Stack>

                    {/* Attendance Based Salary - Yes/No Select */}
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{
                        mt: 1,
                        pt: 1.5,
                        borderTop: `1px dashed ${
                          theme.palette.mode === "dark" ? "#475569" : "#d1d5db"
                        }`,
                      }}
                    >
                      <TextField
                        select
                        label="Attendance Based Salary"
                        value={isAttendanceBased ? "yes" : "no"}
                        onChange={(e) => {
                          const isYes = e.target.value === "yes";
                          setIsAttendanceBased(isYes);

                          // Use the values from formData or attendanceSummary
                          const present =
                            Number(attendanceSummary.total_present_days) || 0;
                          const absent =
                            Number(attendanceSummary.total_absent_days) || 0;
                          const leave =
                            Number(attendanceSummary.total_leave_days) || 0;
                          const workingDays = present + absent + leave; // workingDays = present + absent + leave
                          const grossSalary =
                            parseFloat(formData.gross_salary) || 0;

                          if (isYes && workingDays > 0 && grossSalary > 0) {
                            const attendanceBasedSalary =
                              (grossSalary / workingDays) * present;

                            // First update the attendance_based_salary in formData
                            setFormData((prev) => ({
                              ...prev,
                              attendance_based_salary: attendanceBasedSalary,
                            }));

                            // Then recalculate everything with the new value
                            const updatedFormData = {
                              ...formData,
                              attendance_based_salary: attendanceBasedSalary,
                            };
                            const calculated =
                              calculateSalaryComponents(updatedFormData);

                            setFormData((prev) => ({
                              ...prev,
                              attendance_based_salary: attendanceBasedSalary,
                              total_deduction: calculated.totalDeduction,
                              net_salary: calculated.netSalary,
                              total_ctc_component: calculated.totalCtcComponent,
                              tds: calculated.tds,
                              tds_percentage: calculated.tdsPercentage,
                              pf_amount: calculated.pfAmount,
                              pf_percentage: calculated.pfPercentage,
                              esi_amount: calculated.esiAmount,
                              esi_percentage: calculated.esiPercentage,
                              pt_amount: calculated.ptAmount,
                              pt_percentage: calculated.ptPercentage,
                            }));
                          } else {
                            console.log("Resetting attendance based salary");
                            // Reset attendance based salary and recalculate with gross salary
                            const updatedFormData = {
                              ...formData,
                              attendance_based_salary: 0,
                            };
                            const calculated =
                              calculateSalaryComponents(updatedFormData);
                            setFormData((prev) => ({
                              ...prev,
                              attendance_based_salary: 0,
                              total_deduction: calculated.totalDeduction,
                              net_salary: calculated.netSalary,
                              total_ctc_component: calculated.totalCtcComponent,
                              tds: calculated.tds,
                              tds_percentage: calculated.tdsPercentage,
                              pf_amount: calculated.pfAmount,
                              pf_percentage: calculated.pfPercentage,
                              esi_amount: calculated.esiAmount,
                              esi_percentage: calculated.esiPercentage,
                              pt_amount: calculated.ptAmount,
                              pt_percentage: calculated.ptPercentage,
                            }));
                          }
                        }}
                        size="small"
                        sx={{ ...inputSx, minWidth: 200 }}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="no">No</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                      </TextField>

                      {/* Display the value next to the select */}
                      {formData.attendance_based_salary > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                          }}
                        >
                          {`₹${parseFloat(
                            formData.attendance_based_salary,
                          ).toFixed(2)}`}
                        </Typography>
                      )}
                    </Stack>

                    {/* Editable Attendance Based Salary Field */}
                    {formData.attendance_based_salary > 0 && (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: `1px dashed ${
                            theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db"
                          }`,
                        }}
                      >
                        <TextField
                          label="Attendance Based Salary (₹)"
                          name="attendance_based_salary"
                          type="text"
                          value={
                            formData.attendance_based_salary
                              ? parseFloat(
                                  formData.attendance_based_salary,
                                ).toFixed(2)
                              : "0.00"
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(
                              /[^0-9.]/g,
                              "",
                            );
                            const numValue = parseFloat(rawValue);

                            if (rawValue === "" || rawValue === ".") {
                              setFormData((prev) => ({
                                ...prev,
                                attendance_based_salary: rawValue,
                              }));
                            } else if (!isNaN(numValue)) {
                              setFormData((prev) => ({
                                ...prev,
                                attendance_based_salary: numValue,
                              }));
                            }
                          }}
                          onBlur={() => {
                            if (
                              formData.attendance_based_salary &&
                              !isNaN(formData.attendance_based_salary)
                            ) {
                              setFormData((prev) => ({
                                ...prev,
                                attendance_based_salary: parseFloat(
                                  prev.attendance_based_salary,
                                ),
                              }));

                              // Recalculate with the new value
                              const updatedFormData = {
                                ...formData,
                                attendance_based_salary: parseFloat(
                                  formData.attendance_based_salary,
                                ),
                              };
                              const calculated =
                                calculateSalaryComponents(updatedFormData);
                              setFormData((prev) => ({
                                ...prev,
                                total_deduction: calculated.totalDeduction,
                                net_salary: calculated.netSalary,
                                total_ctc_component:
                                  calculated.totalCtcComponent,
                                tds: calculated.tds,
                                tds_percentage: calculated.tdsPercentage,
                                pf_amount: calculated.pfAmount,
                                pf_percentage: calculated.pfPercentage,
                                esi_amount: calculated.esiAmount,
                                esi_percentage: calculated.esiPercentage,
                                pt_amount: calculated.ptAmount,
                                pt_percentage: calculated.ptPercentage,
                              }));
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                          size="small"
                          sx={{
                            ...inputSx,
                            width: 370,
                            "& .MuiInputBase-root": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#0f151f"
                                  : "#f9fafb",
                              fontWeight: 700,
                              color: "primary.main",
                            },
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                    )}

                    {/* Gross Salary */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: `1px dashed ${
                          theme.palette.mode === "dark" ? "#475569" : "#d1d5db"
                        }`,
                      }}
                    >
                      <TextField
                        label="Gross Salary (₹)"
                        name="gross_salary"
                        type="text"
                        onWheel={(e) => e.target.blur()}
                        value={formData.gross_salary}
                        onChange={handleChange}
                        size="small"
                        sx={{ ...inputSx, width: 370 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  </Box>

                  {/* Deduction Settings Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <CalculateIcon
                        sx={{ color: "primary.main", fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                        }}
                      >
                        Deduction Settings
                      </Typography>
                    </Box>
                    <Stack spacing={1.5}>
                      {/* TDS Row */}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Box sx={{ pt: "30px" }}>
                          <ToggleButtonGroup
                            value={formData.tds_type}
                            exclusive
                            onChange={(e, val) =>
                              handleToggleChange("tds_type", val)
                            }
                            size="large"
                            sx={{
                              height: 40,
                              "& .MuiToggleButton-root": {
                                borderColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.12)"
                                    : "rgba(0,0,0,0.12)",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#9ca3af"
                                    : "#4b5563",
                                "&.Mui-selected": {
                                  backgroundColor: "#f59e0b",
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "#d97706",
                                  },
                                },
                              },
                            }}
                          >
                            <ToggleButton value="percentage" sx={{ width: 50 }}>
                              %
                            </ToggleButton>
                            <ToggleButton value="number" sx={{ width: 50 }}>
                              ₹
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <TextField
                          label="TDS Percentage (%)"
                          name="tds_percentage"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.tds_percentage}
                          onChange={handleChange}
                          disabled={
                            formData.tds_type === "number" || !isBasicFilled
                          }
                          size="small"
                          sx={{ ...inputSx, width: 280 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="TDS (₹)"
                          name="tds"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.tds}
                          onChange={handleChange}
                          disabled={
                            formData.tds_type === "percentage" || !isBasicFilled
                          }
                          size="small"
                          sx={{ ...inputSx, width: 280 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>

                      {/* Applicability Switches */}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{ mt: 0.5 }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.pf_applicable === 1}
                              onChange={handleSwitchChange}
                              name="pf_applicable"
                              sx={switchSx}
                              size="small"
                            />
                          }
                          label="PF Applicable"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.esi_applicable === 1}
                              onChange={handleSwitchChange}
                              name="esi_applicable"
                              sx={switchSx}
                              size="small"
                            />
                          }
                          label="ESI Applicable"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.pt_applicable === 1}
                              onChange={handleSwitchChange}
                              name="pt_applicable"
                              sx={switchSx}
                              size="small"
                            />
                          }
                          label="PT Applicable"
                        />
                      </Stack>

                      {/* PF Row (conditional) */}
                      <AnimatePresence>
                        {formData.pf_applicable === 1 && (
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems="flex-start"
                            component={motion.div}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ overflow: "hidden" }}
                          >
                            <Box sx={{ pt: "30px" }}>
                              <ToggleButtonGroup
                                value={formData.pf_type}
                                exclusive
                                onChange={(e, val) =>
                                  handleToggleChange("pf_type", val)
                                }
                                size="large"
                                sx={{
                                  height: 40,
                                  "& .MuiToggleButton-root": {
                                    borderColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.12)"
                                        : "rgba(0,0,0,0.12)",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#4b5563",
                                    "&.Mui-selected": {
                                      backgroundColor: "#f59e0b",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#d97706",
                                      },
                                    },
                                  },
                                }}
                              >
                                <ToggleButton
                                  value="percentage"
                                  sx={{ width: 50 }}
                                >
                                  %
                                </ToggleButton>
                                <ToggleButton value="number" sx={{ width: 50 }}>
                                  ₹
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                            <TextField
                              label="PF Percentage (%)"
                              name="pf_percentage"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.pf_percentage}
                              onChange={handleChange}
                              disabled={
                                formData.pf_type === "number" || !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="PF (₹)"
                              name="pf_amount"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.pf_amount}
                              onChange={handleChange}
                              disabled={
                                formData.pf_type === "percentage" ||
                                !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>
                        )}
                      </AnimatePresence>

                      {/* ESI Row (conditional) */}
                      <AnimatePresence>
                        {formData.esi_applicable === 1 && (
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems="flex-start"
                            component={motion.div}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ overflow: "hidden" }}
                          >
                            <Box sx={{ pt: "30px" }}>
                              <ToggleButtonGroup
                                value={formData.esi_type}
                                exclusive
                                onChange={(e, val) =>
                                  handleToggleChange("esi_type", val)
                                }
                                size="large"
                                sx={{
                                  height: 40,
                                  "& .MuiToggleButton-root": {
                                    borderColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.12)"
                                        : "rgba(0,0,0,0.12)",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#4b5563",
                                    "&.Mui-selected": {
                                      backgroundColor: "#f59e0b",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#d97706",
                                      },
                                    },
                                  },
                                }}
                              >
                                <ToggleButton
                                  value="percentage"
                                  sx={{ width: 50 }}
                                >
                                  %
                                </ToggleButton>
                                <ToggleButton value="number" sx={{ width: 50 }}>
                                  ₹
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                            <TextField
                              label="ESI Percentage (%)"
                              name="esi_percentage"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.esi_percentage}
                              onChange={handleChange}
                              disabled={
                                formData.esi_type === "number" || !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="ESI (₹)"
                              name="esi_amount"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.esi_amount}
                              onChange={handleChange}
                              disabled={
                                formData.esi_type === "percentage" ||
                                !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>
                        )}
                      </AnimatePresence>

                      {/* PT Row (conditional) */}
                      <AnimatePresence>
                        {formData.pt_applicable === 1 && (
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems="flex-start"
                            component={motion.div}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{ overflow: "hidden" }}
                          >
                            <Box sx={{ pt: "30px" }}>
                              <ToggleButtonGroup
                                value={formData.pt_type}
                                exclusive
                                onChange={(e, val) =>
                                  handleToggleChange("pt_type", val)
                                }
                                size="large"
                                sx={{
                                  height: 40,
                                  "& .MuiToggleButton-root": {
                                    borderColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.12)"
                                        : "rgba(0,0,0,0.12)",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#9ca3af"
                                        : "#4b5563",
                                    "&.Mui-selected": {
                                      backgroundColor: "#f59e0b",
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: "#d97706",
                                      },
                                    },
                                  },
                                }}
                              >
                                <ToggleButton
                                  value="percentage"
                                  sx={{ width: 50 }}
                                >
                                  %
                                </ToggleButton>
                                <ToggleButton value="number" sx={{ width: 50 }}>
                                  ₹
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                            <TextField
                              label="PT Percentage (%)"
                              name="pt_percentage"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.pt_percentage}
                              onChange={handleChange}
                              disabled={
                                formData.pt_type === "number" || !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                              label="PT (₹)"
                              name="pt_amount"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.pt_amount}
                              onChange={handleChange}
                              disabled={
                                formData.pt_type === "percentage" ||
                                !isBasicFilled
                              }
                              size="small"
                              sx={{ ...inputSx, width: 280 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>
                        )}
                      </AnimatePresence>
                    </Stack>
                  </Box>

                  {/* Additional Deduction Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <MoneyIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Additional Deduction
                      </Typography>
                    </Stack>

                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          label="Deduction Name"
                          name="deduction_name"
                          value={formData.deduction_name}
                          onChange={handleChange}
                          size="small"
                          sx={{ ...inputSx, width: "100%" }}
                          InputLabelProps={{ shrink: true }}
                          placeholder="e.g., Loan, Advance, etc."
                        />
                        <TextField
                          label="Deduction Amount (₹)"
                          name="deduction_amount"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.deduction_amount}
                          onChange={handleChange}
                          size="small"
                          sx={{ ...inputSx, width: "100%" }}
                          InputLabelProps={{ shrink: true }}
                          placeholder="0.00"
                        />
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Additional Benefits Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <PercentIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Additional Benefits
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          label="Bonus (₹)"
                          name="bonus"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.bonus}
                          onChange={handleChange}
                          size="small"
                          sx={{ ...inputSx, width: 370 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Mediclaim (₹)"
                          name="mediclaim"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.mediclaim}
                          onChange={handleChange}
                          size="small"
                          sx={{ ...inputSx, width: 370 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Salary Summary Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <ReceiptLongIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Salary Summary
                      </Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          label="Total CTC Component (₹)"
                          name="total_ctc_component"
                          type="text"
                          onWheel={(e) => e.target.blur()}
                          value={formData.total_ctc_component}
                          onChange={handleChange}
                          size="small"
                          sx={{ ...inputSx, width: 370 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <Stack
                          direction="row"
                          spacing={3}
                          alignItems="stretch"
                          sx={{ width: "100%" }}
                        >
                          <Stack
                            direction="row"
                            alignItems="stretch"
                            sx={{ width: "100%", gap: 2 }}
                          >
                            <TextField
                              label="Total Deduction (₹)"
                              name="total_deduction"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.total_deduction}
                              onChange={handleChange}
                              size="small"
                              sx={{ ...inputSx, width: 370 }}
                              InputLabelProps={{ shrink: true }}
                            />

                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ mx: 1 }}
                            />

                            <TextField
                              label="Net Salary (₹)"
                              name="net_salary"
                              type="text"
                              onWheel={(e) => e.target.blur()}
                              value={formData.net_salary}
                              onChange={handleChange}
                              size="small"
                              sx={{ ...inputSx, width: 370 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Notes Section */}
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0f151f" : "#f9fafb",
                      p: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <NotesIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontFamily: "monospace",
                          textTransform: "uppercase",
                          color: "primary.main",
                          letterSpacing: "0.5px",
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Notes
                      </Typography>
                    </Stack>
                    <TextField
                      label="Remarks"
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Add any additional notes..."
                      multiline
                      rows={2}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    py: 1,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    minWidth: "auto",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  sx={{
                    borderRadius: "10px",
                    py: 1,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    minWidth: "auto",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Configuration"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditSalaryConfigurationModal;
